import {
  PurchaseOrderRevision,
  PurchaseOrderRevisionStatus,
} from './purchase-order-revision';

export type PrintType = 'hangtag_sticker_status' | 'dual_sticker_status';

export type BulkProdSubStage =
  | 'Ready for Production'
  | 'Buy Materials'
  | 'Cutting/Sewing'
  | 'Washing'
  | 'Packaging'
  | 'Production Completed';

export type Delivery = {
  booking: {
    id: string;
    reference: string;
    starts_at: string;
    updated_at?: string;
    ends_at: string;
  };
  delivered_at?: string | null;
};

export type Bill = {
  internal_id: string;
  invoice_number?: string;
  ns_created_at?: string;
};

export type CatalogItem = {
  size?: string;
  width?: number;
  length?: number;
  height?: number;
  vendor_style_number?: string;
  weight?: number;
  wms_id?: number;
  item_name?: string;
  sku?: string;
  last_po_receipt_date?: string;
};

export type PurchaseOrderLine = {
  id: string;
  po_number?: string;
  po_line_id: number;
  item_name: string;
  vendor_style: string;
  department?: string;
  category?: string;
  sku?: string;
  color_label?: string;
  price?: number;
  quantity: number;
  total?: number;
  top_status?: string;
  upc?: string;
  size?: string;
  quantity_received?: number;
  carton_label_status: string;
  hangtag_sticker_status?: string;
  dual_sticker_status?: string;
  catalog_item: CatalogItem[];
  revision_log_line?: {
    new_value: string | null;
    old_value: string | null;
    field_name: Exclude<
      keyof PurchaseOrderLine,
      'revision_log_line' | 'catalog_item'
    >;
    updated_at: string;
    change_type: 'update' | 'line_added';
  }[];
  prop65: boolean | null;
  size_type?: string;
};

/**
 * This values comes from NetSuite.
 */
export enum PurchaseOrderVendorMerchType {
  THIRD_PARTY = '3rd Party',
  DIRECT = 'Direct',
  HYBRID = 'Hybrid',
  FINISHER = 'Finisher',
  TESTER = 'Tester',
}

export type MarketplaceImage = {
  url: string;
  thumbnail_url: string;
  type: 'front' | 'swatch' | 'fit';
  style_number: string | null;
  product_name: string | null;
};

export type PurchaseOrder = {
  internal_id: string;
  buyer_email: string;
  buyer_name: string;
  due_date: string;
  status: string;
  is_cancelled: boolean;
  stage?: string;
  sub_stage?: string;
  audit_status: string;
  po_number: string;
  purchase_type?: string;
  order_type?: string;
  division?: string;
  incoterm?: string;
  top_due_date: string;
  shipment_mode: string;
  delivery?: Delivery;
  is_accepted?: boolean;
  created_date?: string;
  date_of_acceptance?: string;
  received_date?: string;
  revised_at?: string;
  revision_accepted_at?: string;
  vendor_rejection_reason?: string;
  vendor_message?: string;
  original_due_date?: string;
  fob_ship_date?: string;
  original_fob_ship_date?: string;
  country_of_origin?: string;
  top_required?: boolean;
  images?: string[];
  marketplace_images?: MarketplaceImage[];
  purchase_order_lines: PurchaseOrderLine[];
  bills?: Bill[];
  revision_log_header?: {
    field_name: string;
    new_value: string;
    old_value: string;
    updated_at: string;
    change_type: 'update';
  }[];
  purchase_order_activities?: PurchaseOrderActivity[];
  vendor_revision?: PurchaseOrderRevision;
  vendor_revisions_history?: PurchaseOrderRevision[];
  vendor_merch_type?: PurchaseOrderVendorMerchType;
};

export enum PurchaseOrderActivityType {
  VENDOR_ACCEPTED = 'VENDOR_ACCEPTED',
  VENDOR_REJECTED = 'VENDOR_REJECTED',
  CANCELLED = 'CANCELLED',
  VENDOR_UPLOADED_INVOICE = 'VENDOR_UPLOADED_INVOICE',
  VENDOR_UPLOADED_PACKING_LIST = 'VENDOR_UPLOADED_PACKING_LIST',
  STATUS_UPDATED = 'STATUS_UPDATED',
  VENDOR_TOP_SHIPPED = 'VENDOR_TOP_SHIPPED',
  DELIVERY_SCHEDULED = 'DELIVERY_SCHEDULED',
  DELIVERY_UPDATED = 'DELIVERY_UPDATED',
  DELIVERY_CANCELLED = 'DELIVERY_CANCELLED',
  DELIVERY_RECEIVED = 'DELIVERY_RECEIVED',
  RECEIVING_DELIVERY = 'RECEIVING_DELIVERY',
  REMOVED_FROM_DELIVERY = 'REMOVED_FROM_DELIVERY',
  DELIVERY_SHIPPED = 'DELIVERY_SHIPPED',
  CATALOG_ITEM_CREATED = 'CATALOG_ITEM_CREATED',
  CATALOG_ITEM_UPDATED = 'CATALOG_ITEM_UPDATED',
  PURCHASE_ORDER_CREATED = 'PURCHASE_ORDER_CREATED',
  PURCHASE_ORDER_UPDATED = 'PURCHASE_ORDER_UPDATED',
  PURCHASE_ORDER_LINE_CREATED = 'PURCHASE_ORDER_LINE_CREATED',
  PURCHASE_ORDER_LINE_UPDATED = 'PURCHASE_ORDER_LINE_UPDATED',
  PURCHASE_ORDER_LINES_UPDATED = 'PURCHASE_ORDER_LINES_UPDATED',
  BULK_PRODUCTION_STAGE_UPDATED = 'BULK_PRODUCTION_STAGE_UPDATED',
  BULK_PRODUCTION_COMPLETED = 'BULK_PRODUCTION_COMPLETED',
  TOP_RECEIVED = 'TOP_RECEIVED',
  TOP_APPROVED = 'TOP_APPROVED',
  TOP_PARTIALLY_APPROVED = 'TOP_PARTIALLY_APPROVED',
  TOP_REJECTED = 'TOP_REJECTED',
  TOP_PARTIALLY_REJECTED = 'TOP_PARTIALLY_REJECTED',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  PRE_PROD_APPROVED = 'PRE_PROD_APPROVED',
  TOP_PARTIALLY_RECEIVED = 'TOP_PARTIALLY_RECEIVED',
  TOP_NOT_REQUIRED = 'TOP_NOT_REQUIRED',
  PROP_65_SET = 'PROP_65_SET',
  DELIVERY_CHECK_IN = 'DELIVERY_CHECK_IN',
  REVISION_REQUESTED = 'REVISION_REQUESTED',
  REVISION_STATUS_UPDATED = 'REVISION_STATUS_UPDATED',
  REVISION_UPDATED = 'REVISION_UPDATED',
  CANCELLED_REOPENED = 'CANCELLED_REOPENED',
}

export const PurchaseOrderRevisionStatusMap: Partial<
  Record<PurchaseOrderRevisionStatus, string>
> = {
  [PurchaseOrderRevisionStatus.REVISION_PD_IN_NEGOTIATION]: 'PD In Negotiation',
  [PurchaseOrderRevisionStatus.REVISION_REQUESTED]: 'Confirmed by PD',
  [PurchaseOrderRevisionStatus.REVISION_NEGOTIATION]: 'in Negotiation',
  [PurchaseOrderRevisionStatus.REVISION_READY_FOR_NS_UPDATE]: 'Approved',
  [PurchaseOrderRevisionStatus.REVISION_APPROVED]: 'Approved',
  [PurchaseOrderRevisionStatus.REVISION_COMPLETED]: 'Completed',
  [PurchaseOrderRevisionStatus.REVISION_CANCELLED]: 'Cancelled',
  [PurchaseOrderRevisionStatus.REVISION_CANCELLED_BY_NS]: 'Cancelled completed',
  [PurchaseOrderRevisionStatus.REVISION_REJECTED]: 'Rejected',
};

export enum PurchaseOrderActivityAuthorType {
  VP = 'vp',
  INTERNAL = 'internal',
}

export type PurchaseOrderActivityAuthor = {
  full_name?: string;
  identifier?: string;
  role?: string;
  type: PurchaseOrderActivityAuthorType;
};

export type PurchaseOrderActivity = {
  id: string;
  author: PurchaseOrderActivityAuthor;
  po_line_number: number | null;
  type: PurchaseOrderActivityType;
  details: Record<string, any> | null;
  created_at: string;
  deleted_at: string | null;
};

export type PurchaseOrderLineFieldNamesType = {
  [key in keyof PurchaseOrderLine]?: string;
};
export const purchaseOrderLineFieldNames: PurchaseOrderLineFieldNamesType = {
  item_name: 'Item Name',
  vendor_style: 'Vendor Style',
  sku: 'SKU',
  upc: 'UPC',
  color_label: 'Color Label',
  size: 'Size',
  price: 'Price',
  quantity: 'Quantity',
  quantity_received: 'Quantity Received',
  total: 'Total',
  carton_label_status: 'Carton Label Status',
  hangtag_sticker_status: 'Hangtag Sticker Status',
  dual_sticker_status: 'Dual Sticker Status',
};
